import React, { Component, ErrorInfo, ReactNode } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { i18n } from 'utils/i18n';

interface Props {
  children: ReactNode;
  /** UI alternativo a mostrar cuando hay un error. Si no se especifica, muestra el fallback por defecto. */
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary] Uncaught error:', error.message, info.componentStack);
  }

  handleReset = () => {
    this.setState({ hasError: false });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          height="100%"
          minHeight={200}
          gap={2}
          p={4}
        >
          <Typography variant="h6" color="error">
            {i18n('error.boundary.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {i18n('error.boundary.description')}
          </Typography>
          <Button variant="outlined" size="small" onClick={this.handleReset}>
            {i18n('error.boundary.retry')}
          </Button>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
